import { ShopifyProduct, ShopifyVariant, ShopifyOrderInfo } from '../types/shopify';

/** Shown when Shopify order/product data cannot be loaded — avoid exposing API errors. */
export const SHOPIFY_DATA_UNAVAILABLE_MESSAGE =
  "We're unable to load order details right now. Please reach out to support for help.";

/**
 * Calculates the maximum price and variant count for a product
 */
export const calculateProductPricing = (
  product: ShopifyProduct,
  selectedVariant: ShopifyVariant | null,
  availableVariantIds: string[] = []
): { maxPrice: string | null; variantCount: number } => {
  if (selectedVariant) return { maxPrice: null, variantCount: 0 };
    
  const availableVariants = availableVariantIds.length > 0
    ? product.variants.filter(variant => availableVariantIds.includes(variant.id))
    : product.variants;

  if (availableVariants.length === 0) return { maxPrice: null, variantCount: 0 };
  
  const max = availableVariants.reduce((max, variant) => {
    const price = parseFloat(variant.price);
    return price > max ? price : max;
  }, 0).toFixed(2);

  return { 
    maxPrice: max,
    variantCount: availableVariants.length
  };
};

/**
 * Extracts variant tags from a variant's selected options
 */
export const getVariantTags = (variant: ShopifyVariant | null): string[] => {
  return variant?.selectedOptions
    .map(option => option.value)
    .filter(value => value !== "Default Title") || [];
};

/**
 * Gets the fulfillment status display properties
 */
export const getFulfillmentStatusProps = (status: string): { 
  colorScheme: string;
  label: string;
} => {
  switch (status.toUpperCase()) {
    case 'UNFULFILLED':
      return { colorScheme: 'yellow', label: 'Preparing Order' };
    case 'FULFILLED':
      return { colorScheme: 'green', label: 'Shipped' };
    case 'PARTIALLY_FULFILLED':
      return { colorScheme: 'orange', label: 'Partially Shipped' };
    default:
      return { colorScheme: 'gray', label: status };
  }
};

/**
 * Formats a shipping address into a readable string
 */
export const formatShippingAddress = (address: {
  address1: string;
  address2?: string;
  city: string;
  zip: string;
  country: string;
}): string[] => {
  const formattedAddress = [
    address.address1,
    address.address2,
    `${address.city}, ${address.zip}`,
    address.country
  ];
  
  return formattedAddress.filter(Boolean);
};

/**
 * Checks if an order has any tracking information
 */
export const hasTrackingInfo = (order: ShopifyOrderInfo['order']): boolean => {
  return order.fulfillments.some(f => f.trackingInfo.length > 0);
};

/**
 * Gets the first available image URL and alt text for a product
 */
export const getProductMainImage = (product: ShopifyProduct): {
  url: string;
  altText: string;
} | null => {
  if (!product.images?.[0]) return null;
  
  return {
    url: product.images[0].url,
    altText: product.images[0].altText || product.title
  };
}; 